// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for the WS8a LOCKED canvas notation (Canvas + AI Build Brief,
// 2026-07-30). Single source of truth for the notation colors + polarity glyphs
// so the SVG renderers and the CSS stay in agreement.
//
// The locked rules this encodes:
//   - Link bodies are NEUTRAL (no polarity color). Color is NOT on the links.
//   - Polarity is shown by a LABEL at the arrowhead: default +/-, with a display
//     toggle to S/O (same/opposite).
//   - Line style: SOLID = a regular link (BOTH + and - are solid). DASHED is
//     reserved for delay / information-flow ONLY (never dashed-for-negative);
//     the delay mark is dashed + a double-slash. (Delay rendering is gated on
//     link-delay data that the CLD path does not yet surface -- tracked separately.)
//   - Color lives in the LOOP: a bold cyan/slate badge (R#/B#) at the loop
//     center -- the "Digital Harmony Duo" -- inverting by paper.
//
// Paper inversion: light (white/cream/plain) vs black (neon). The badge and the
// link/label neutrals both invert so they read on either ground.
//
// IMPORTANT: these hex values are mirrored in CLDLoopLabel.module.css and
// CLDArrow.module.css (CSS can't import TS constants). If a value changes here,
// change it there in the same commit.

/** Feedback-loop sign (reinforcing = R, balancing = B). */
export type LoopSign = 'reinforcing' | 'balancing' | 'unknown';

/** Single-link polarity surfaced from the engine's link analysis. */
export type LinkPolarity = 'positive' | 'negative' | 'unknown';

/** Canvas ground: light (white/cream/plain) or black (neon). */
export type PaperKind = 'light' | 'black';

/** How the polarity label reads: +/- (sign) or S/O (same/opposite). */
export type PolarityDisplay = 'sign' | 'so';

/**
 * Loop badge colors -- the "Digital Harmony Duo" (locked 2026-07-30). Cyan =
 * reinforcing, slate = balancing; inverts by paper so the badge stays bold on
 * either ground. Unknown is a neutral gray. Text color pairs: white text on the
 * light-paper fills, black text on the black-paper fills.
 */
export const LOOP_COLORS: Readonly<Record<PaperKind, Readonly<Record<LoopSign, string>>>> = {
  light: { reinforcing: '#0891B2', balancing: '#475569', unknown: '#6B7280' },
  black: { reinforcing: '#22D3EE', balancing: '#CBD5E1', unknown: '#9CA3AF' },
};

/** Contrasting badge text color for each paper (white on light, black on black). */
export const LOOP_TEXT_COLOR: Readonly<Record<PaperKind, string>> = {
  light: '#FFFFFF',
  black: '#000000',
};

/**
 * Extra hues for loops beyond the top reinforcing / top balancing pair (Williams
 * 2026-08-07): amber, violet, green, blue, red. The dominant R stays cyan and the
 * dominant B stays slate (LOOP_COLORS); these distinguish additional loops so the
 * canvas badge/ring and the Loop Picker row read as the same loop. Paper-aware,
 * like LOOP_COLORS -- lighter on black paper so they hold up on the neon ground.
 */
export const LOOP_PALETTE: Readonly<Record<PaperKind, readonly string[]>> = {
  light: ['#D97706', '#7C3AED', '#059669', '#2563EB', '#DC2626'],
  black: ['#FBBF24', '#A78BFA', '#34D399', '#60A5FA', '#F87171'],
};

/**
 * Assign a badge/ring fill to every loop, keyed by loop id, so the canvas and the
 * Loop Picker color each loop identically (Williams 2026-08-07: "used the same way
 * in the picker and on the canvas"). The FIRST reinforcing loop gets cyan and the
 * FIRST balancing loop gets slate (the locked Digital Harmony Duo); every other
 * loop takes the next distinct hue from LOOP_PALETTE; unknown-polarity loops stay
 * neutral gray. Pass loops in ranked order so "first" means "top".
 */
export function assignLoopColors(
  loops: readonly { readonly id: string; readonly polarity: LoopSign }[],
  paper: PaperKind = 'light',
): Map<string, string> {
  const out = new Map<string, string>();
  const palette = LOOP_PALETTE[paper];
  let seenReinforcing = false;
  let seenBalancing = false;
  let paletteIndex = 0;
  for (const loop of loops) {
    let fill: string;
    if (loop.polarity === 'reinforcing' && !seenReinforcing) {
      fill = LOOP_COLORS[paper].reinforcing;
      seenReinforcing = true;
    } else if (loop.polarity === 'balancing' && !seenBalancing) {
      fill = LOOP_COLORS[paper].balancing;
      seenBalancing = true;
    } else if (loop.polarity === 'unknown') {
      fill = LOOP_COLORS[paper].unknown;
    } else {
      fill = palette[paletteIndex % palette.length];
      paletteIndex += 1;
    }
    out.set(loop.id, fill);
  }
  return out;
}

/**
 * Neutral link/label ink. Near-slate on light paper; a light slate on black
 * paper so the neutral links stay visible on the neon ground. Polarity is never
 * carried by this color -- both + and - links use it.
 */
export const LINK_NEUTRAL: Readonly<Record<PaperKind, string>> = {
  light: '#334155',
  black: '#CBD5E1',
};

/** Resolve the loop badge fill for a sign on a given paper. */
export function loopColor(sign: LoopSign, paper: PaperKind = 'light'): string {
  return LOOP_COLORS[paper][sign] ?? LOOP_COLORS[paper].unknown;
}

/** Map an Editor `canvasPaper` preference to the notation's paper kind. */
export function paperKind(canvasPaper: 'white' | 'yellow' | 'green' | 'black' | undefined): PaperKind {
  // The light tints (white/yellow/green) all use the light-paper notation; only
  // dark mode ('black') inverts. (Two-axis model, Will 08-03/08-06.)
  return canvasPaper === 'black' ? 'black' : 'light';
}

/**
 * The label shown at a link's arrowhead. Sign mode is the default (+/-); the S/O
 * toggle reads Same/Opposite. Unknown polarity is always '?'. (The S/O toggle is
 * a canvas display option that lands with the Settings surface, CO-3; the
 * renderer defaults to sign until then.)
 */
export function polarityGlyph(polarity: LinkPolarity, display: PolarityDisplay = 'sign'): string {
  if (polarity === 'unknown') {
    return '?';
  }
  if (display === 'so') {
    return polarity === 'positive' ? 'S' : 'O';
  }
  return polarity === 'positive' ? '+' : '−'; // U+2212 MINUS, visually balanced vs ASCII '-'
}

/** Loop label text: `R1` reinforcing, `B2` balancing, `?` unknown. */
export function loopLabel(sign: LoopSign, index: number): string {
  switch (sign) {
    case 'reinforcing':
      return `R${index}`;
    case 'balancing':
      return `B${index}`;
    default:
      return '?';
  }
}
